import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Calendar } from "@/components/ui/calendar.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import { UserDetailsCard } from "@/components/users/user-details/user-details-card.tsx";
import {
  useUpdateEmail,
  useUpdatePhoneNumber,
  useUpdateProfilePhoto,
} from "@/lib/api/user";
import { User } from "@/lib/api/user.ts";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user.ts";
import { cn, getInitials } from "@/lib/utils.ts";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { ProfilePhotoDialog } from "./profile-photo-dialog";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PhoneChangeModal } from "./phone-change-modal";
import { EmailChangeModal } from "./email-change-modal";

interface UserDetailsProps {
  user: User;
}

export function UserDetails({ user }: UserDetailsProps) {
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canUpdateUsers =
    hasPrivilegedAccess && ability.can("update", "users");
  const canUpdateProfileMedia =
    hasPrivilegedAccess && ability.can("update", "users.profile-media");

  const redactedFields = new Set(user.redacted_fields ?? []);
  const redacted = {
    firstName: redactedFields.has("first_name"),
    lastName: redactedFields.has("last_name"),
    phone: redactedFields.has("phone"),
    email: redactedFields.has("email"),
    birthday: redactedFields.has("birthday"),
    photo: redactedFields.has("profile_picture_url"),
  };
  const { handleUpdate, form, fieldState } = useUpdateUser({
    userId: user.id,
    firstName: user.first_name ?? undefined,
    lastName: user.last_name ?? undefined,
    email: user.email ?? undefined,
    phone: user.phone ?? undefined,
    birthday: user.birthday
      ? new Date(user.birthday.toString().replace(/-/g, "/"))
      : undefined,
  });

  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const updateProfilePhoto = useUpdateProfilePhoto(user.id);
  const updatePhoneNumber = useUpdatePhoneNumber(user.id);
  const updateEmail = useUpdateEmail(user.id);

  const { url: photoUrl } = useSignedMedia(
    redacted.photo ? "" : (user.profile_picture_url ?? ""),
    user.id,
  );

  return (
    <UserDetailsCard>
      <div className="mb-8 flex flex-col items-center justify-center gap-4">
        <Avatar className="h-20 w-20">
          {!redacted.photo && (
            <AvatarImage src={photoUrl} alt={user.name ?? ""} />
          )}
          <AvatarFallback>
            {redacted.photo ? "?" : getInitials(user.name ?? "")}
          </AvatarFallback>
        </Avatar>
        {!redacted.photo && (
          <Button
            variant="outline"
            onClick={() => setIsPhotoDialogOpen(true)}
            className="w-fit"
            disabled={!canUpdateProfileMedia}
          >
            Change Photo
          </Button>
        )}
      </div>

      <ProfilePhotoDialog
        open={isPhotoDialogOpen}
        onClose={() => setIsPhotoDialogOpen(false)}
        onUpload={async (file) => {
          await updateProfilePhoto.mutateAsync(file);
        }}
        userName={user.name ?? undefined}
      />

      <Form {...form}>
        <form className="flex flex-col gap-4">
          <FormField
            disabled={
              !canUpdateUsers || fieldState.firstName || redacted.firstName
            }
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">First name</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      redacted.firstName ? "[redacted]" : "Enter first name"
                    }
                    {...field}
                    value={redacted.firstName ? "" : (field.value ?? "")}
                    onBlur={() => {
                      handleUpdate("firstName", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            disabled={
              !canUpdateUsers || fieldState.lastName || redacted.lastName
            }
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Last name</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      redacted.lastName ? "[redacted]" : "Enter last name"
                    }
                    {...field}
                    value={redacted.lastName ? "" : (field.value ?? "")}
                    onBlur={() => {
                      handleUpdate("lastName", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="birthday"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-neutral-500">
                  Date of birth
                </FormLabel>

                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                        disabled={!canUpdateUsers || redacted.birthday}
                      >
                        {redacted.birthday ? (
                          <span className="italic">[redacted]</span>
                        ) : field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      defaultMonth={field.value}
                      onSelect={(date) => {
                        if (!canUpdateUsers) return;
                        field.onChange(date);

                        let updateDate;
                        if (date) {
                          updateDate = new Date(date);
                        }

                        handleUpdate("birthday", updateDate);
                      }}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            disabled
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Phone number</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder={redacted.phone ? "[redacted]" : "Enter phone"}
                      inputMode="tel"
                      {...field}
                      value={redacted.phone ? "" : (field.value ?? "")}
                    />
                    {!redacted.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        disabled={!canUpdateUsers}
                        onClick={() => {
                          setIsPhoneModalOpen(true);
                        }}
                      >
                        <span className="hidden sm:inline">
                          Change Phone Number
                        </span>
                        <span className="sm:hidden">Change</span>
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            disabled
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Email</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder={redacted.email ? "[redacted]" : "Enter email"}
                      inputMode="email"
                      {...field}
                      value={redacted.email ? "" : (field.value ?? "")}
                    />
                    {!redacted.email && (
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        disabled={!canUpdateUsers}
                        onClick={() => {
                          setIsEmailModalOpen(true);
                        }}
                      >
                        <span className="hidden sm:inline">Change Email</span>
                        <span className="sm:hidden">Change Email</span>
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </form>

        <PhoneChangeModal
          open={isPhoneModalOpen}
          onClose={() => setIsPhoneModalOpen(false)}
          onSubmit={async (phoneNumber) => {
            await updatePhoneNumber.mutateAsync(phoneNumber);
            form.setValue("phone", phoneNumber);
            setIsPhoneModalOpen(false);
          }}
          currentPhone={user.phone ?? undefined}
        />

        <EmailChangeModal
          open={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={async (email) => {
            await updateEmail.mutateAsync(email);
            form.setValue("email", email);
            setIsEmailModalOpen(false);
          }}
          currentEmail={user.email ?? undefined}
        />
      </Form>
    </UserDetailsCard>
  );
}
